// @givmo/compliance — the review's way in (CCM-R1-REVIEW-WEB; frame 91): one card at the top of the
// Calendar, and on the first visit, while the organization's newest revision holds something open.
//
// The card reads the newest revision and never makes one: the review is generated where it opens.
// With nothing open there is no card, and its absence claims nothing (no "nothing to file"). An
// organization that never opened the review has no revision, so no card (a note on
// `CCM-R1-ASSESS-RULES`, the orchestrator's Q9: no generating here). It counts what the review page
// leaves open, so it reads the calendar's filings too, under the key every page reads them by: a
// filing already on the calendar is no longer waiting.
//
// CCM-R1-REVIEW-ASSIGN (frame 108): the person asked reads who asked them about how many items,
// above N2, once per asker, newest first. Every open ask counts, those on items the review doesn't
// show now too: the page lists them last (U23, D-CCM-60). A change to a filing on the calendar counts
// in N2 (U28 (B)), so the card shows when changes are all that's open.

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Button, Card, Icon, testIdProp } from '@givmo/design-system';
import { isGivmoError } from '@givmo/api-client';
import { ClipboardList } from 'lucide-react';
import { useCompliance } from './compliance-context';
import { extractObligations } from './compliance-decode';
import { listAllObligations, listAllProposalItems, listAllReviewAsks } from './compliance-queries';
import { COMPLIANCE_ROUTES } from './compliance-routes';
import {
  decodeAsk,
  decodeItem,
  decodeProposal,
  type ReviewAsk,
  type ReviewItem,
} from './review-decode';
import { foundWords, nameOrSomeone } from './review-model';
import { REVIEW_BUTTON, REVIEW_TITLE, askedYouAbout, reviewCardLine } from './review-words';

const PREFIX = 'compliance-review-entry';

/** The query key the card reads under; the review page's act moves it. */
export function reviewEntryQueryKey(orgRef: string | null) {
  return ['compliance', 'review', 'entry', orgRef] as const;
}

/** The asks on the viewer, read under one key the review page's acts move. */
export function reviewAsksQueryKey(orgRef: string | null, userId: string | undefined) {
  return ['compliance', 'review', 'asks', orgRef, userId, 'open'] as const;
}

/** Frame 108's lines: each asker, newest first, with how many items they asked about. */
export function askedYouLines(
  asks: readonly ReviewAsk[],
  personName: (userId: string) => string | undefined,
): string[] {
  const byAsker = new Map<string, { items: Set<string>; newest: string }>();
  for (const ask of asks) {
    if (ask.state !== 'open') continue;
    const entry = byAsker.get(ask.askedByUserId) ?? { items: new Set<string>(), newest: '' };
    entry.items.add(ask.itemKey);
    if (ask.askedAt > entry.newest) entry.newest = ask.askedAt;
    byAsker.set(ask.askedByUserId, entry);
  }
  return [...byAsker.entries()]
    .sort(([, a], [, b]) => b.newest.localeCompare(a.newest))
    .map(([askerId, entry]) => askedYouAbout(nameOrSomeone(personName, askerId), entry.items.size));
}

export function ReviewEntryCard() {
  const { orgRef, operations, moduleStanding, isReady, viewerUserId, personName } = useCompliance();
  const navigate = useNavigate();
  const live = isReady && moduleStanding === 'available' && orgRef !== null;
  const read = useQuery({
    queryKey: reviewEntryQueryKey(orgRef),
    enabled: live,
    queryFn: async (): Promise<ReviewItem[] | null> => {
      let revisionId: string | undefined;
      try {
        revisionId = decodeProposal(await operations.getProposal(orgRef ?? ''))?.revisionId;
      } catch (error) {
        // No proposal yet (or the review dark): nothing to say, and nothing claimed.
        if (isGivmoError(error) && error.status === 404) return null;
        throw error;
      }
      if (revisionId === undefined) return null;
      const rows = await listAllProposalItems(operations, orgRef ?? '', revisionId);
      return rows.map(decodeItem).filter((item): item is ReviewItem => item !== undefined);
    },
  });
  const obligations = useQuery({
    queryKey: ['compliance', 'obligations', orgRef],
    queryFn: () => listAllObligations(operations, orgRef ?? ''),
    enabled: live,
  });
  const asks = useQuery({
    queryKey: reviewAsksQueryKey(orgRef, viewerUserId),
    queryFn: () =>
      listAllReviewAsks(operations, orgRef ?? '', { askedUserId: viewerUserId, state: 'open' }),
    enabled: live && viewerUserId !== undefined,
  });
  if (read.data === undefined || read.data === null || obligations.data === undefined) return null;
  const confirmed = extractObligations(obligations.data).filter(
    (obligation) => obligation.candidateState === 'confirmed',
  );
  // H-c: a filing whose tracking stopped is off the calendar, so the review's offer of it counts,
  // whatever was decided on it before the stop (O3).
  const onCalendar = new Set(
    confirmed
      .filter((obligation) => obligation.trackingStoppedAt === undefined)
      .map((obligation) => obligation.obligationId),
  );
  const stoppedAt = new Map(
    confirmed.flatMap((obligation) =>
      obligation.trackingStoppedAt !== undefined
        ? [[obligation.obligationId, obligation.trackingStoppedAt] as const]
        : [],
    ),
  );
  const found = foundWords(read.data, onCalendar, stoppedAt);
  const asked = askedYouLines(
    (asks.data ?? []).map(decodeAsk).filter((ask): ask is ReviewAsk => ask !== undefined),
    (userId) => personName?.(userId),
  );
  if (found === undefined && asked.length === 0) return null;
  return (
    <Card className="flex items-center gap-16 px-20 py-16" testId={PREFIX}>
      <span className="flex size-36 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
        <Icon icon={ClipboardList} size={18} aria-hidden />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <h2 className="text-s15 font-w600 text-text-strong">{REVIEW_TITLE}</h2>
        {asked.map((line) => (
          <p
            key={line}
            className="text-s13 font-w600 leading-h15 text-text"
            {...testIdProp(`${PREFIX}-asked`)}
          >
            {line}
          </p>
        ))}
        {found !== undefined && (
          <p className="text-s13 leading-h15 text-text-secondary" {...testIdProp(`${PREFIX}-line`)}>
            {reviewCardLine(found.words, found.total)}
          </p>
        )}
      </div>
      <Button
        appearance="saas"
        tone="secondary"
        size="sm"
        onClick={() => void navigate({ to: COMPLIANCE_ROUTES.review })}
        testId={`${PREFIX}-open`}
      >
        {REVIEW_BUTTON}
      </Button>
    </Card>
  );
}
